import React, { useState } from 'react';
import { 
  LayoutDashboard, 
  Receipt, 
  FileText, 
  FolderKanban, 
  BarChart3, 
  Building2, 
  Users, 
  Tag,
  ChevronLeft,
  ChevronRight,
  Menu,
  X
} from 'lucide-react';

interface CompanyTheme {
  name: string;
  logoUrl?: string;
  logoText: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  textOnPrimary: string;
}

const themes: CompanyTheme[] = [
  {
    name: 'Kessan Default',
    logoText: 'K',
    primaryColor: '#3B82F6',
    secondaryColor: '#1E40AF',
    accentColor: '#60A5FA',
    textOnPrimary: '#FFFFFF',
  },
  {
    name: 'Empresa Verde',
    logoText: 'EV',
    primaryColor: '#10B981',
    secondaryColor: '#059669',
    accentColor: '#34D399',
    textOnPrimary: '#FFFFFF',
  },
  {
    name: 'Tech Purple',
    logoText: 'TP',
    primaryColor: '#8B5CF6',
    secondaryColor: '#6D28D9',
    accentColor: '#A78BFA',
    textOnPrimary: '#FFFFFF',
  },
  {
    name: 'Corporate Dark',
    logoText: 'CD',
    primaryColor: '#1F2937',
    secondaryColor: '#111827',
    accentColor: '#374151',
    textOnPrimary: '#FFFFFF',
  },
  {
    name: 'Sunset Orange',
    logoText: 'SO',
    primaryColor: '#F97316',
    secondaryColor: '#EA580C',
    accentColor: '#FB923C',
    textOnPrimary: '#FFFFFF',
  },
];

const ThemePrototype: React.FC = () => {
  const [currentTheme, setCurrentTheme] = useState<CompanyTheme>(themes[0]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('dashboard');

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, section: 'main' },
    { id: 'expenses', label: 'Gastos', icon: Receipt, section: 'main' },
    { id: 'invoices', label: 'Facturas', icon: FileText, section: 'main' },
    { id: 'projects', label: 'Proyectos', icon: FolderKanban, section: 'main' },
    { id: 'reports', label: 'Reportes', icon: BarChart3, section: 'analysis' },
    { id: 'company', label: 'Mi Empresa', icon: Building2, section: 'config' },
    { id: 'categories', label: 'Categorías', icon: Tag, section: 'config' },
    { id: 'users', label: 'Usuarios', icon: Users, section: 'config' },
  ];

  const sections = [
    { id: 'main', label: 'OPERACIONES', items: menuItems.filter(i => i.section === 'main') },
    { id: 'analysis', label: 'ANÁLISIS', items: menuItems.filter(i => i.section === 'analysis') },
    { id: 'config', label: 'CONFIGURACIÓN', items: menuItems.filter(i => i.section === 'config') },
  ];

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Theme Selector - Fixed at top */}
      <div className="bg-white border-b border-gray-200 p-4 z-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">
            🎨 Selector de Tema - Prototipo Visual
          </h2>
          <div className="flex flex-wrap gap-2">
            {themes.map((theme) => (
              <button
                key={theme.name}
                onClick={() => setCurrentTheme(theme)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  currentTheme.name === theme.name
                    ? 'ring-2 ring-offset-2 shadow-lg scale-105'
                    : 'hover:scale-105'
                }`}
                style={{
                  backgroundColor: theme.primaryColor,
                  color: theme.textOnPrimary,
                }}
              >
                {theme.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - Desktop */}
        <aside
          className={`hidden lg:flex flex-col transition-all duration-300 ${
            sidebarCollapsed ? 'w-20' : 'w-64'
          }`}
          style={{
            backgroundColor: currentTheme.primaryColor,
            color: currentTheme.textOnPrimary,
          }}
        >
          {/* Logo Section */}
          <div className="p-6 border-b border-white/10">
            <div className="flex items-center justify-between">
              {!sidebarCollapsed ? (
                <div className="flex items-center space-x-3">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl font-bold shadow-lg"
                    style={{
                      backgroundColor: currentTheme.secondaryColor,
                      color: currentTheme.textOnPrimary,
                    }}
                  >
                    {currentTheme.logoText}
                  </div>
                  <div>
                    <h1 className="text-xl font-bold">Kessan</h1>
                    <p className="text-xs opacity-80">Gestión de Gastos</p>
                  </div>
                </div>
              ) : (
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl font-bold shadow-lg mx-auto"
                  style={{
                    backgroundColor: currentTheme.secondaryColor,
                    color: currentTheme.textOnPrimary,
                  }}
                >
                  {currentTheme.logoText}
                </div>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-6">
            {sections.map((section) => (
              <div key={section.id}>
                {!sidebarCollapsed && (
                  <h3 className="text-xs font-semibold opacity-60 mb-2 px-3">
                    {section.label}
                  </h3>
                )}
                <div className="space-y-1">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeSection === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => setActiveSection(item.id)}
                        className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all ${
                          isActive
                            ? 'shadow-lg scale-105'
                            : 'hover:bg-white/10'
                        }`}
                        style={{
                          backgroundColor: isActive ? currentTheme.accentColor : 'transparent',
                        }}
                        title={sidebarCollapsed ? item.label : undefined}
                      >
                        <Icon className="w-5 h-5 flex-shrink-0" />
                        {!sidebarCollapsed && (
                          <span className="font-medium">{item.label}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* Collapse Button */}
          <div className="p-4 border-t border-white/10">
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="w-full flex items-center justify-center p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              {sidebarCollapsed ? (
                <ChevronRight className="w-5 h-5" />
              ) : (
                <ChevronLeft className="w-5 h-5" />
              )}
            </button>
          </div>
        </aside>

        {/* Mobile Sidebar */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-50">
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setMobileMenuOpen(false)}
            />
            <aside
              className="absolute left-0 top-0 bottom-0 w-64 flex flex-col"
              style={{
                backgroundColor: currentTheme.primaryColor,
                color: currentTheme.textOnPrimary,
              }}
            >
              {/* Logo Section */}
              <div className="p-6 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl font-bold shadow-lg"
                    style={{
                      backgroundColor: currentTheme.secondaryColor,
                      color: currentTheme.textOnPrimary,
                    }}
                  >
                    {currentTheme.logoText}
                  </div>
                  <div>
                    <h1 className="text-xl font-bold">Kessan</h1>
                    <p className="text-xs opacity-80">Gestión de Gastos</p>
                  </div>
                </div>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 hover:bg-white/10 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Navigation */}
              <nav className="flex-1 overflow-y-auto p-4 space-y-6">
                {sections.map((section) => (
                  <div key={section.id}>
                    <h3 className="text-xs font-semibold opacity-60 mb-2 px-3">
                      {section.label}
                    </h3>
                    <div className="space-y-1">
                      {section.items.map((item) => {
                        const Icon = item.icon;
                        const isActive = activeSection === item.id;
                        return (
                          <button
                            key={item.id}
                            onClick={() => {
                              setActiveSection(item.id);
                              setMobileMenuOpen(false);
                            }}
                            className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all ${
                              isActive
                                ? 'shadow-lg scale-105'
                                : 'hover:bg-white/10'
                            }`}
                            style={{
                              backgroundColor: isActive ? currentTheme.accentColor : 'transparent',
                            }}
                          >
                            <Icon className="w-5 h-5 flex-shrink-0" />
                            <span className="font-medium">{item.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </nav>
            </aside>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <header className="bg-white border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setMobileMenuOpen(true)}
                  className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
                >
                  <Menu className="w-6 h-6" />
                </button>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {menuItems.find(i => i.id === activeSection)?.label || 'Dashboard'}
                  </h2>
                  <p className="text-sm text-gray-500">
                    Tema actual: {currentTheme.name}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="text-sm font-semibold text-gray-600">JD</span>
                  </div>
                  <div className="hidden md:block">
                    <p className="text-sm font-semibold text-gray-900">John Doe</p>
                    <p className="text-xs text-gray-500">Admin</p>
                  </div>
                </div>
              </div>
            </div>
          </header>

          {/* Content Area */}
          <main className="flex-1 overflow-y-auto p-6">
            <div className="max-w-7xl mx-auto space-y-6">
              {/* Demo Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Total Gastos</h3>
                    <div
                      className="w-12 h-12 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${currentTheme.primaryColor}20` }}
                    >
                      <Receipt className="w-6 h-6" style={{ color: currentTheme.primaryColor }} />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">$2,450,000</p>
                  <p className="text-sm text-gray-500 mt-2">+12% vs mes anterior</p>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Proyectos Activos</h3>
                    <div
                      className="w-12 h-12 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${currentTheme.primaryColor}20` }}
                    >
                      <FolderKanban className="w-6 h-6" style={{ color: currentTheme.primaryColor }} />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">8</p>
                  <p className="text-sm text-gray-500 mt-2">2 finalizados este mes</p>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Facturas Pendientes</h3>
                    <div
                      className="w-12 h-12 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${currentTheme.primaryColor}20` }}
                    >
                      <FileText className="w-6 h-6" style={{ color: currentTheme.primaryColor }} />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">5</p>
                  <p className="text-sm text-gray-500 mt-2">$850,000 total</p>
                </div>
              </div>

              {/* Demo Content */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Vista Previa del Contenido
                </h3>
                <div className="space-y-4">
                  <p className="text-gray-600">
                    Este es un prototipo visual del nuevo diseño con sidebar personalizable.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      className="px-4 py-2 rounded-lg font-medium transition-all hover:shadow-lg"
                      style={{
                        backgroundColor: currentTheme.primaryColor,
                        color: currentTheme.textOnPrimary,
                      }}
                    >
                      Botón Primario
                    </button>
                    <button
                      className="px-4 py-2 rounded-lg font-medium transition-all hover:shadow-lg"
                      style={{
                        backgroundColor: currentTheme.secondaryColor,
                        color: currentTheme.textOnPrimary,
                      }}
                    >
                      Botón Secundario
                    </button>
                    <button
                      className="px-4 py-2 rounded-lg font-medium transition-all hover:shadow-lg"
                      style={{
                        backgroundColor: currentTheme.accentColor,
                        color: currentTheme.textOnPrimary,
                      }}
                    >
                      Botón Accent
                    </button>
                  </div>
                </div>
              </div>

              {/* Color Palette */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Paleta de Colores Actual
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <div
                      className="w-full h-24 rounded-lg mb-2"
                      style={{ backgroundColor: currentTheme.primaryColor }}
                    />
                    <p className="text-sm font-medium text-gray-900">Primary</p>
                    <p className="text-xs text-gray-500">{currentTheme.primaryColor}</p>
                  </div>
                  <div>
                    <div
                      className="w-full h-24 rounded-lg mb-2"
                      style={{ backgroundColor: currentTheme.secondaryColor }}
                    />
                    <p className="text-sm font-medium text-gray-900">Secondary</p>
                    <p className="text-xs text-gray-500">{currentTheme.secondaryColor}</p>
                  </div>
                  <div>
                    <div
                      className="w-full h-24 rounded-lg mb-2"
                      style={{ backgroundColor: currentTheme.accentColor }}
                    />
                    <p className="text-sm font-medium text-gray-900">Accent</p>
                    <p className="text-xs text-gray-500">{currentTheme.accentColor}</p>
                  </div>
                  <div>
                    <div
                      className="w-full h-24 rounded-lg mb-2 border border-gray-200"
                      style={{ backgroundColor: currentTheme.textOnPrimary }}
                    />
                    <p className="text-sm font-medium text-gray-900">Text on Primary</p>
                    <p className="text-xs text-gray-500">{currentTheme.textOnPrimary}</p>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default ThemePrototype;
